import { EcommerceAPIApi as EcommerceApi, type TouchlessTicket, type TouchlessTicketSaveRequest, type RequestTicketSaveRequest, type VehicleInfo } from '@parkingbase/ecommerce-api-test';
import type { RawAxiosRequestConfig } from 'axios';
import { PAYMENT_TIMEOUT_MS } from '@/lib/api/payment-timeout';
import { BaseApiService } from './baseApi.service';

export class TouchlessService extends BaseApiService {
  protected ticketApi: EcommerceApi;
  protected transactionApi: EcommerceApi;

  constructor() {
    super();
    this.ticketApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
    this.transactionApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
  }

  /**
   * Reads the touchless ticket for the scan-to-pay screen.
   *
   * ## Invariants:
   * - Uses the shared client's default timeout (`DEFAULT_API_TIMEOUT_MS`, 120 s) — do NOT give it
   *   a shorter one. Why: for a logged-in parker whose discount programs carry usage limits the api
   *   takes 100 s+ on this read (PB-5122). PB-4915's 30 s timeout failed every such load while the
   *   api was still working.
   * - Not retried automatically. Why: a replay after a timeout starts a second copy of that slow
   *   work and re-sends a single-use reCAPTCHA token the api then refuses. The page's "Try again"
   *   popup is the retry, and it mints a fresh token.
   */
  async getTicketById(
    ticketId: string,
    endTime?: string,
    companyId?: string,
    options?: RawAxiosRequestConfig
  ) {
    const response = await this.ticketApi.getTouchlessTicketById(
      ticketId,
      endTime,
      companyId,
      options
    );
    return response.data;
  }

  async submitPayment(saveRequest: TouchlessTicketSaveRequest, options?: RawAxiosRequestConfig) {
    const response = await this.ticketApi.touchlessTicketPayment(saveRequest, {
      ...options,
      timeout: PAYMENT_TIMEOUT_MS,
    });
    return response.data;
  }

  async decodeQrCode(touchlessTicket: TouchlessTicket) {
    const response = await this.ticketApi.decodeQrCode(touchlessTicket);
    return response.data;
  }

  async getCompanyInfoFromUrl(url: string) {
    const response = await this.ticketApi.getCompanyInfoFromTouchlessUrl(url);
    return response.data;
  }

  async requestPickup(request: RequestTicketSaveRequest) {
    const response = await this.ticketApi.requestTicket(request);
    return response.data;
  }

  async updateVehicleForTicket(
    company: string,
    ticketId: string,
    vehicleInfo: VehicleInfo,
    options?: RawAxiosRequestConfig
  ) {
    const response = await this.ticketApi.updateVehicleDetailsForTicket(
      company,
      ticketId,
      vehicleInfo,
      options
    );
    return response.data;
  }

  async downloadReceipt(transactionId: string, options?: RawAxiosRequestConfig) {
    const response = await this.transactionApi.getTouchlessTransactionReceipt(transactionId, {
      ...options,
      responseType: 'blob',
    });
    return response.data;
  }
}
